import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

// ── Dialing code → Country name + flag emoji mapping ──────────────────
const COUNTRY_CODE_MAP: Record<string, { name: string; flag: string }> = {
  "+1": { name: "United States", flag: "🇺🇸" },
  "+7": { name: "Russia", flag: "🇷🇺" },
  "+20": { name: "Egypt", flag: "🇪🇬" },
  "+27": { name: "South Africa", flag: "🇿🇦" },
  "+30": { name: "Greece", flag: "🇬🇷" },
  "+31": { name: "Netherlands", flag: "🇳🇱" },
  "+32": { name: "Belgium", flag: "🇧🇪" },
  "+33": { name: "France", flag: "🇫🇷" },
  "+34": { name: "Spain", flag: "🇪🇸" },
  "+36": { name: "Hungary", flag: "🇭🇺" },
  "+39": { name: "Italy", flag: "🇮🇹" },
  "+40": { name: "Romania", flag: "🇷🇴" },
  "+41": { name: "Switzerland", flag: "🇨🇭" },
  "+43": { name: "Austria", flag: "🇦🇹" },
  "+44": { name: "United Kingdom", flag: "🇬🇧" },
  "+45": { name: "Denmark", flag: "🇩🇰" },
  "+46": { name: "Sweden", flag: "🇸🇪" },
  "+47": { name: "Norway", flag: "🇳🇴" },
  "+48": { name: "Poland", flag: "🇵🇱" },
  "+49": { name: "Germany", flag: "🇩🇪" },
  "+51": { name: "Peru", flag: "🇵🇪" },
  "+52": { name: "Mexico", flag: "🇲🇽" },
  "+53": { name: "Cuba", flag: "🇨🇺" },
  "+54": { name: "Argentina", flag: "🇦🇷" },
  "+55": { name: "Brazil", flag: "🇧🇷" },
  "+56": { name: "Chile", flag: "🇨🇱" },
  "+57": { name: "Colombia", flag: "🇨🇴" },
  "+58": { name: "Venezuela", flag: "🇻🇪" },
  "+60": { name: "Malaysia", flag: "🇲🇾" },
  "+61": { name: "Australia", flag: "🇦🇺" },
  "+62": { name: "Indonesia", flag: "🇮🇩" },
  "+63": { name: "Philippines", flag: "🇵🇭" },
  "+64": { name: "New Zealand", flag: "🇳🇿" },
  "+65": { name: "Singapore", flag: "🇸🇬" },
  "+66": { name: "Thailand", flag: "🇹🇭" },
  "+81": { name: "Japan", flag: "🇯🇵" },
  "+82": { name: "South Korea", flag: "🇰🇷" },
  "+84": { name: "Vietnam", flag: "🇻🇳" },
  "+86": { name: "China", flag: "🇨🇳" },
  "+90": { name: "Turkey", flag: "🇹🇷" },
  "+91": { name: "India", flag: "🇮🇳" },
  "+92": { name: "Pakistan", flag: "🇵🇰" },
  "+93": { name: "Afghanistan", flag: "🇦🇫" },
  "+94": { name: "Sri Lanka", flag: "🇱🇰" },
  "+95": { name: "Myanmar", flag: "🇲🇲" },
  "+98": { name: "Iran", flag: "🇮🇷" },
  "+212": { name: "Morocco", flag: "🇲🇦" },
  "+213": { name: "Algeria", flag: "🇩🇿" },
  "+216": { name: "Tunisia", flag: "🇹🇳" },
  "+218": { name: "Libya", flag: "🇱🇾" },
  "+220": { name: "Gambia", flag: "🇬🇲" },
  "+221": { name: "Senegal", flag: "🇸🇳" },
  "+233": { name: "Ghana", flag: "🇬🇭" },
  "+234": { name: "Nigeria", flag: "🇳🇬" },
  "+254": { name: "Kenya", flag: "🇰🇪" },
  "+255": { name: "Tanzania", flag: "🇹🇿" },
  "+256": { name: "Uganda", flag: "🇺🇬" },
  "+260": { name: "Zambia", flag: "🇿🇲" },
  "+263": { name: "Zimbabwe", flag: "🇿🇼" },
  "+351": { name: "Portugal", flag: "🇵🇹" },
  "+352": { name: "Luxembourg", flag: "🇱🇺" },
  "+353": { name: "Ireland", flag: "🇮🇪" },
  "+354": { name: "Iceland", flag: "🇮🇸" },
  "+358": { name: "Finland", flag: "🇫🇮" },
  "+370": { name: "Lithuania", flag: "🇱🇹" },
  "+371": { name: "Latvia", flag: "🇱🇻" },
  "+372": { name: "Estonia", flag: "🇪🇪" },
  "+380": { name: "Ukraine", flag: "🇺🇦" },
  "+381": { name: "Serbia", flag: "🇷🇸" },
  "+385": { name: "Croatia", flag: "🇭🇷" },
  "+420": { name: "Czech Republic", flag: "🇨🇿" },
  "+421": { name: "Slovakia", flag: "🇸🇰" },
  "+880": { name: "Bangladesh", flag: "🇧🇩" },
  "+960": { name: "Maldives", flag: "🇲🇻" },
  "+961": { name: "Lebanon", flag: "🇱🇧" },
  "+962": { name: "Jordan", flag: "🇯🇴" },
  "+963": { name: "Syria", flag: "🇸🇾" },
  "+964": { name: "Iraq", flag: "🇮🇶" },
  "+965": { name: "Kuwait", flag: "🇰🇼" },
  "+966": { name: "Saudi Arabia", flag: "🇸🇦" },
  "+971": { name: "UAE", flag: "🇦🇪" },
  "+972": { name: "Israel", flag: "🇮🇱" },
  "+974": { name: "Qatar", flag: "🇶🇦" },
  "+977": { name: "Nepal", flag: "🇳🇵" },
};

const COLOR_PALETTE = [
  "#0284C7", // Sky blue
  "#6366F1", // Indigo
  "#0D9488", // Teal
  "#F59E0B", // Amber
  "#EC4899", // Rose / Pink
  "#8B5CF6", // Purple
  "#10B981", // Emerald
  "#F97316", // Orange
  "#06B6D4", // Cyan
  "#84CC16", // Lime
  "#3B82F6", // Blue
  "#E11D48", // Crimson
  "#14B8A6", // Turquoise
  "#A855F7", // Violet
  "#64748B", // Slate
  "#475569", // Dark Slate
];

const regionDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });

function getFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "🌍";
  return String.fromCodePoint(
    ...countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0)),
  );
}

function resolveCountryInfo(raw: string): { name: string; flag: string } {
  const trimmed = (raw || "").trim();
  if (
    !trimmed ||
    trimmed.toLowerCase() === "other" ||
    trimmed.toLowerCase() === "unknown"
  ) {
    return { name: "Other / Unspecified", flag: "🌍" };
  }

  if (trimmed.startsWith("+")) {
    const entry = COUNTRY_CODE_MAP[trimmed];
    if (entry) {
      return { name: entry.name, flag: entry.flag };
    }
    return { name: `Dialing (${trimmed})`, flag: "🌍" };
  }

  if (trimmed.length === 2) {
    const upper = trimmed.toUpperCase();
    try {
      const regionName = regionDisplayNames.of(upper);
      if (regionName) {
        return { name: regionName, flag: getFlagEmoji(upper) };
      }
    } catch {
      // ignore
    }
    return { name: upper, flag: getFlagEmoji(upper) };
  }

  return { name: trimmed, flag: "🌍" };
}

type Props = {
  className: string;
  labels: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

type FilterLimit = 6 | 12 | "all";

type CountryItem = {
  id: string;
  name: string;
  flag: string;
  value: number;
  percent: string;
  color: string;
};

const CountryWiseUsersChart: React.FC<Props> = ({
  className,
  labels,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();
  const [filterLimit, setFilterLimit] = useState<FilterLimit>(6);

  // Group and sort country counts descending
  const allCountries = useMemo(() => {
    const map = new Map<string, { name: string; flag: string; value: number }>();
    for (let i = 0; i < (labels || []).length; i++) {
      const raw = labels[i];
      const count = Number(dataList?.[i]) || 0;
      if (!raw || count <= 0) continue;

      const info = resolveCountryInfo(raw);
      const existing = map.get(info.name);
      if (existing) {
        existing.value += count;
      } else {
        map.set(info.name, { name: info.name, flag: info.flag, value: count });
      }
    }

    const list = Array.from(map.values());
    list.sort((a, b) => b.value - a.value);
    return list;
  }, [labels, dataList]);

  const totalUsers = useMemo(() => {
    return allCountries.reduce((sum, c) => sum + c.value, 0);
  }, [allCountries]);

  const hasData = totalUsers > 0;

  const filterOptions = useMemo(() => {
    const total = allCountries.length;
    if (total <= 6) return [];
    const options: { label: string; value: FilterLimit }[] = [
      { label: "Top 6", value: 6 },
    ];
    if (total > 6 && total <= 12) {
      options.push({ label: `All (${total})`, value: "all" });
    } else if (total > 12) {
      options.push({ label: "Top 12", value: 12 });
      options.push({ label: `All (${total})`, value: "all" });
    }
    return options;
  }, [allCountries.length]);

  // Items to display in donut and breakdown cards
  const displayItems: CountryItem[] = useMemo(() => {
    if (!allCountries.length) return [];

    const limit = filterLimit === "all" ? allCountries.length : filterLimit;
    if (allCountries.length <= limit) {
      return allCountries.map((c, idx) => ({
        id: c.name,
        name: c.name,
        flag: c.flag,
        value: c.value,
        percent:
          totalUsers > 0 ? ((c.value / totalUsers) * 100).toFixed(1) : "0",
        color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
      }));
    }

    // Top N - 1 items + remaining grouped
    const topItems = allCountries.slice(0, limit - 1);
    const remainingSum = allCountries
      .slice(limit - 1)
      .reduce((acc, c) => acc + c.value, 0);
    const remainingCount = allCountries.length - (limit - 1);

    const result: CountryItem[] = topItems.map((c, idx) => ({
      id: c.name,
      name: c.name,
      flag: c.flag,
      value: c.value,
      percent:
        totalUsers > 0 ? ((c.value / totalUsers) * 100).toFixed(1) : "0",
      color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
    }));

    if (remainingSum > 0) {
      result.push({
        id: "remaining",
        name: `Other (${remainingCount} countries)`,
        flag: "🌍",
        value: remainingSum,
        percent:
          totalUsers > 0 ? ((remainingSum / totalUsers) * 100).toFixed(1) : "0",
        color: COLOR_PALETTE[(limit - 1) % COLOR_PALETTE.length],
      });
    }

    return result;
  }, [allCountries, filterLimit, totalUsers]);

  useEffect(() => {
    if (!chartRef.current || !displayItems.length || !hasData) {
      return;
    }

    if (chartRef.current) {
      chartRef.current.innerHTML = "";
    }

    const seriesData = displayItems.map((item) => item.value);
    const categoryLabels = displayItems.map((item) => item.name);
    const categoryFlags = displayItems.map((item) => item.flag);
    const colors = displayItems.map((item) => item.color);

    const chart = new ApexCharts(
      chartRef.current,
      getDonutChartOptions(
        240,
        categoryLabels,
        seriesData,
        categoryFlags,
        colors,
        totalUsers,
        mode,
      ),
    );
    chart.render();

    return () => {
      chart.destroy();
      if (chartRef.current) {
        chartRef.current.innerHTML = "";
      }
    };
  }, [chartRef, mode, displayItems, hasData, totalUsers]);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div
        className="card-header border-0 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 pb-2"
        style={{ position: "relative", zIndex: 15 }}
      >
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ??
              "See where your strongest user clusters are building across countries."}
          </p>
        </div>
        {filterOptions.length > 0 && (
          <div
            className="d-flex align-items-center gap-2 flex-wrap ms-md-auto"
            style={{ position: "relative", zIndex: 20 }}
          >
            <span className="text-muted fw-bold fs-8 d-none d-sm-inline">
              View:
            </span>
            <div
              className="dashboard-chart-filter-group"
              style={{ position: "relative", zIndex: 20 }}
            >
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`dashboard-chart-filter-btn ${
                    filterLimit === opt.value ? "active" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFilterLimit(opt.value);
                  }}
                  style={{
                    cursor: "pointer",
                    pointerEvents: "auto",
                    position: "relative",
                    zIndex: 25,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card-body dashboard-chart-card__body pt-2 pb-5">
        {!hasData ? (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">CW</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No country distribution yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                User activity will appear here as soon as data is available.
              </span>
            </span>
          </div>
        ) : null}

        <div className="d-flex flex-column align-items-center w-100">
          <div
            ref={chartRef}
            className="dashboard-chart-canvas d-flex justify-content-center"
            style={{
              height: "240px",
              width: "100%",
              opacity: hasData ? 1 : 0.2,
            }}
          ></div>

          <div
            className={`dashboard-country-grid ${
              displayItems.length > 6 ? "dashboard-country-scrollable" : ""
            }`}
          >
            {displayItems.map((item, idx) => (
              <div key={item.id + idx} className="dashboard-country-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-2 min-w-0">
                    <span
                      className="dashboard-country-dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="dashboard-country-flag">{item.flag}</span>
                    <span
                      className="dashboard-country-name text-truncate"
                      title={item.name}
                    >
                      {item.name}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                    <span className="dashboard-country-count">
                      {item.value.toLocaleString()}
                    </span>
                    <span className="dashboard-country-pct">
                      {item.percent}%
                    </span>
                  </div>
                </div>
                <div className="dashboard-country-progress">
                  <div
                    className="dashboard-country-progress-bar"
                    style={{
                      width: `${Math.max(3, parseFloat(item.percent))}%`,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { CountryWiseUsersChart };

function getDonutChartOptions(
  height: number,
  labels: string[],
  dataList: number[],
  flags: string[],
  colors: string[],
  total: number,
  mode: string,
): ApexOptions {
  const isDark = mode === "dark";

  return {
    series: dataList,
    chart: {
      fontFamily: "inherit",
      type: "donut",
      height: height,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
        dynamicAnimation: {
          enabled: true,
          speed: 300,
        },
      },
    },
    labels: labels,
    colors: colors,
    plotOptions: {
      pie: {
        expandOnClick: true,
        customScale: 0.98,
        donut: {
          size: "68%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "12px",
              fontWeight: 700,
              color: isDark ? "#94A3B8" : "#64748B",
              offsetY: -8,
            },
            value: {
              show: true,
              fontSize: "24px",
              fontWeight: 800,
              color: isDark ? "#FFFFFF" : "#0F172A",
              offsetY: 4,
              formatter: function (val: string) {
                return Number(val).toLocaleString();
              },
            },
            total: {
              show: true,
              showAlways: true,
              label: "Total Users",
              fontSize: "11px",
              fontWeight: 700,
              color: isDark ? "#94A3B8" : "#64748B",
              formatter: function () {
                return Number(total).toLocaleString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    stroke: {
      width: 3,
      colors: [isDark ? "#1E1E2D" : "#ffffff"],
    },
    tooltip: {
      enabled: true,
      theme: isDark ? "dark" : "light",
      custom: function ({ series, seriesIndex }) {
        const name = labels[seriesIndex] || "Unknown";
        const flag = flags[seriesIndex] || "🌍";
        const val = series[seriesIndex] || 0;
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
        const sliceColor = colors[seriesIndex] || "#0284C7";

        const bg = isDark ? "#1e1e2d" : "#ffffff";
        const textCol = isDark ? "#ffffff" : "#0f172a";
        const borderCol = isDark ? "#2b2b40" : "#e2e8f0";

        return (
          '<div style="padding: 10px 14px; font-family: inherit; font-size: 12px; background: ' +
          bg +
          "; border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.15); border: 1px solid " +
          borderCol +
          '; min-width: 170px; pointer-events: none;">' +
          '<div style="display: flex; align-items: center; gap: 8px; font-weight: 700; color: ' +
          textCol +
          '; font-size: 13px; margin-bottom: 5px;">' +
          '<span style="font-size: 16px;">' +
          flag +
          "</span>" +
          "<span>" +
          escapeHtml(name) +
          "</span>" +
          "</div>" +
          '<div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">' +
          '<span style="color: ' +
          sliceColor +
          '; font-weight: 800; font-size: 14px;">' +
          Number(val).toLocaleString() +
          " users</span>" +
          '<span style="background: rgba(14, 116, 144, 0.12); color: #0284c7; padding: 2px 6px; border-radius: 6px; font-weight: 700; font-size: 11px;">' +
          pct +
          "%</span>" +
          "</div>" +
          "</div>"
        );
      },
    },
  };
}

function escapeHtml(str: string): string {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

